import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

type TabsSize = "md" | "sm";

const TabsSizeContext = React.createContext<TabsSize>("md");

const tabsListVariants = cva(
  "inline-flex items-center gap-1 rounded-lg bg-[#F4F4F6] p-1",
  {
    variants: {
      size: {
        md: "h-10",
        sm: "h-8",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 font-inter font-medium text-neutral-600 ring-offset-white transition-[background-color,color,box-shadow,font-weight] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-neutral-800 data-[state=active]:font-semibold data-[state=active]:shadow-[0_1px_2px_rgba(16,24,40,0.05)] [&_i]:leading-none [&_i]:text-neutral-600 [&_svg]:shrink-0 [&_svg]:text-neutral-600 data-[state=active]:[&_i]:text-blue-500 data-[state=active]:[&_svg]:text-blue-500",
  {
    variants: {
      size: {
        md: "h-8 text-sm leading-5 [&_i]:text-[20px] [&_svg]:size-5",
        sm: "h-6 text-xs leading-4 [&_i]:text-xs [&_svg]:size-3",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

interface TabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, size, ...props }, ref) => {
  const resolvedSize = size ?? "md";

  return (
    <TabsSizeContext.Provider value={resolvedSize}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ size: resolvedSize }), className)}
        {...props}
      />
    </TabsSizeContext.Provider>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, size, ...props }, ref) => {
  const inheritedSize = React.useContext(TabsSizeContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        tabsTriggerVariants({ size: size ?? inheritedSize }),
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
